import React, { useEffect, useState } from "react";
import { Box, IconButton, InputBase, FormHelperText, Button, Typography, useTheme, TextField } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import styles from "./styles";
import { AlertBar } from "../../components";
import { ChatMessage } from "../../types/Chatbot/Chat";
import ChatResponse from "./ChatResponse";
import EndChatModal from "./EndChatModal";

interface ChatbotProps {
  onClose: () => void;
  isError: boolean;
  errMessage: string;
  setIsError: any;
  handleEndChat: () => void;
  messages: ChatMessage[];
  handleSendMessage: (question: string) => void;
  isLoading: boolean;
  withHeader: boolean;
}

const Chatbot: React.FC<ChatbotProps> = ({ onClose, isError, errMessage, setIsError, handleEndChat, messages, handleSendMessage, isLoading, withHeader }) => {
  // console.log("Rendering Chatbot with messages:", messages);
  const theme = useTheme();
  const [input, setInput] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false); // Tracks modal visibility

  // Function to close the modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Function to handle end chat action
  const handleEndChatAction = () => {
    handleEndChat(); // Clears all chat messages and resets the state
    setIsError(false); // Clears error state
    setIsModalOpen(false); // Closes the modal
  };

  // Clear input when loading starts
  useEffect(() => {
    if (isLoading) {
      setInput("");
    }
  }, [isLoading]);

  return (
    <Box sx={withHeader ? styles.chatbotContainer(theme) : { width: "760px" }}>
      {/* Chat Header */}
      {withHeader && (
        <Box sx={styles.chatbotHeader(theme)}>
          <Button
            variant="text"
            color="primary"
            size="medium"
            sx={{ fontWeight: "bold" }}
            onClick={() => setIsModalOpen(true)} // Open modal
          >
            END CHAT
          </Button>
          <IconButton size="medium" onClick={onClose}>
            <KeyboardArrowDownIcon />
          </IconButton>
        </Box>
      )}

      {/* Error Alert */}
      {isError && (
        <Box sx={{ ...styles.containerSpacing, mb: 2 }}>
          <AlertBar description={errMessage} severity="error" setShow={setIsError} show={isError} />
        </Box>
      )}

      {/* Chat Body */}
      <Box sx={withHeader ? styles.chatbotBody(theme) : styles.noHeaderContainer}>
        <ChatResponse messages={messages} isLoading={isLoading} miniChat={withHeader} />
      </Box>

      {/* Input Field */}
      <Box sx={withHeader ? styles.chatbotInputContainer(theme) : styles.noHeaderInput}>
        <TextField
          disabled={isLoading}
          label={withHeader ? "Message PACE AI" : "Message Insurtech Solutions"}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && input.trim() !== "") {
              handleSendMessage(input);
            }
          }}
          variant="outlined"
          fullWidth
          sx={styles.inputBase}
          InputProps={{
            endAdornment: (
              <IconButton onClick={() => handleSendMessage(input)} disabled={isLoading || input.trim() === ""} sx={styles.sendButton}>
                <SendIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />
        <FormHelperText sx={styles.helperText(theme)}>Generated content may contain inaccuracies; make sure to verify information.</FormHelperText>
      </Box>

      {/* End Chat Modal */}
      {withHeader && (
        <EndChatModal
          isOpen={isModalOpen}
          onClose={handleCloseModal} // Close modal without clearing messages
          onConfirm={handleEndChatAction} // Clears chat and closes modal
        />
      )}
    </Box>
  );
};

export default Chatbot;
